/**
 * BYOB Cart Guard v5
 * Enforces minimum bundle size on cart page, cart drawer, AND checkout.
 *
 * Protection layers:
 * 1. Click interception (capture phase) on remove/decrease/checkout buttons
 * 2. Input change interception on quantity fields
 * 3. Form submit interception — blocks checkout if bundle < min
 * 4. Fetch monkey-patch on /cart/change.js — catches ALL cart changes
 *
 * Always active on every page (drawer can appear anywhere).
 */
(function () {
  'use strict';

  var MIN_ITEMS = window.BYOB_CART_GUARD_MIN || 4;
  var BUNDLE_PROP = '_bundle';
  var BUNDLE_VAL = 'byob-hipp';

  var cartData = null;
  var isProcessing = false;

  /* ─── Helpers ─── */

  function fetchCartData() {
    return fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' }
    }).then(function (r) { return r.json(); });
  }

  function refreshCart() {
    fetchCartData().then(function (cart) { cartData = cart; });
  }

  function isBundleItem(item) {
    if (!item.properties) return false;
    return item.properties[BUNDLE_PROP] === BUNDLE_VAL;
  }

  function getBundleItems(cart) {
    if (!cart || !cart.items) return [];
    return cart.items.filter(isBundleItem);
  }

  function getBundleCount(cart) {
    var items = getBundleItems(cart);
    var count = 0;
    for (var i = 0; i < items.length; i++) count += items[i].quantity;
    return count;
  }

  // Check if a response looks like a full cart object (not /cart/add.js response)
  function isFullCart(data) {
    return data && typeof data.item_count === 'number' && Array.isArray(data.items);
  }

  function removeAllBundleItems() {
    if (isProcessing) return;
    isProcessing = true;

    fetchCartData().then(function (cart) {
      var items = getBundleItems(cart);
      if (!items.length) {
        isProcessing = false;
        return;
      }
      var updates = {};
      for (var i = 0; i < items.length; i++) {
        updates[items[i].key] = 0;
      }
      return fetch('/cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: updates })
      });
    }).then(function () {
      isProcessing = false;
      window.location.reload();
    }).catch(function () {
      isProcessing = false;
      window.location.reload();
    });
  }

  /* ─── Bundle key checks ─── */

  function isKeyBundle(key) {
    if (!cartData) return false;
    var items = getBundleItems(cartData);
    for (var i = 0; i < items.length; i++) {
      if (items[i].key === key) return true;
    }
    return false;
  }

  function computeNewBundleCount(key, action) {
    if (!cartData) return MIN_ITEMS;
    var items = getBundleItems(cartData);
    var count = 0;
    for (var i = 0; i < items.length; i++) {
      if (items[i].key === key) {
        if (action === 'remove') continue;
        if (action === 'decrease') count += Math.max(0, items[i].quantity - 1);
        else count += items[i].quantity;
      } else {
        count += items[i].quantity;
      }
    }
    return count;
  }

  function computeCountForQuantity(key, newQty) {
    if (!cartData) return MIN_ITEMS;
    var items = getBundleItems(cartData);
    var count = 0;
    for (var i = 0; i < items.length; i++) {
      if (items[i].key === key) {
        count += newQty;
      } else {
        count += items[i].quantity;
      }
    }
    return count;
  }

  /* ═══════════════════════════════════════════
     Layer 1: Intercept clicks (capture phase)
     ═══════════════════════════════════════════ */

  function interceptClick(e) {
    if (isProcessing || !cartData) return;

    var bundleCount = getBundleCount(cartData);
    if (bundleCount === 0) return;

    var target = e.target;

    // ── Block checkout buttons when bundle is below minimum ──
    var checkoutBtn = target.closest('[name="checkout"], .checkout__button, [data-cart-checkout], a[href="/checkout"], .checkout-btn, [class*="checkout"]');
    if (checkoutBtn && bundleCount > 0 && bundleCount < MIN_ITEMS) {
      e.preventDefault();
      e.stopImmediatePropagation();
      alert('Your HiPP bundle requires at least ' + MIN_ITEMS + ' items. You currently have ' + bundleCount + '. Please add ' + (MIN_ITEMS - bundleCount) + ' more item(s) or remove all bundle items before checking out.');
      return false;
    }

    // ── Remove button (trash icon or "Remove" link) ──
    var removeEl = target.closest('[data-remove-key]');
    if (removeEl) {
      var key = removeEl.getAttribute('data-remove-key');
      if (isKeyBundle(key)) {
        var newCount = computeNewBundleCount(key, 'remove');
        if (newCount > 0 && newCount < MIN_ITEMS) {
          e.preventDefault();
          e.stopImmediatePropagation();
          if (confirm('Removing this item will drop your bundle below ' + MIN_ITEMS + ' items. This will remove all bundle items from your cart. Continue?')) {
            removeAllBundleItems();
          }
          return false;
        }
      }
      return;
    }

    // ── Decrease button ──
    var decreaseBtn = target.closest('[data-decrease-quantity]');
    if (decreaseBtn) {
      var row = decreaseBtn.closest('[data-cart-item]');
      if (!row) return;
      var input = row.querySelector('[data-update-cart]');
      if (!input) return;
      var dKey = input.getAttribute('data-update-cart');
      if (!dKey || !isKeyBundle(dKey)) return;

      var currentQty = parseInt(input.value, 10) || 0;
      var action = currentQty <= 1 ? 'remove' : 'decrease';
      var dNewCount = computeNewBundleCount(dKey, action);

      if (dNewCount > 0 && dNewCount < MIN_ITEMS) {
        e.preventDefault();
        e.stopImmediatePropagation();
        if (confirm('Reducing this quantity will drop your bundle below ' + MIN_ITEMS + ' items. This will remove all bundle items from your cart. Continue?')) {
          removeAllBundleItems();
        }
        return false;
      }
    }
  }

  /* ═══════════════════════════════════════════
     Layer 2: Guard quantity input direct edits
     ═══════════════════════════════════════════ */

  function guardInput(e) {
    if (isProcessing || !cartData) return;
    if (getBundleItems(cartData).length === 0) return;

    var input = e.target.closest('[data-update-cart]');
    if (!input) return;
    var key = input.getAttribute('data-update-cart');
    if (!key || !isKeyBundle(key)) return;

    var newQty = parseInt(input.value, 10) || 0;
    var newCount = computeCountForQuantity(key, newQty);

    if (newCount > 0 && newCount < MIN_ITEMS) {
      e.preventDefault();
      e.stopImmediatePropagation();
      if (confirm('Changing this quantity will drop your bundle below ' + MIN_ITEMS + ' items. This will remove all bundle items from your cart. Continue?')) {
        removeAllBundleItems();
      } else {
        for (var j = 0; j < cartData.items.length; j++) {
          if (cartData.items[j].key === key) {
            input.value = cartData.items[j].quantity;
            break;
          }
        }
      }
      return false;
    }
  }

  /* ═══════════════════════════════════════════
     Layer 3: Block form submissions to checkout
     ═══════════════════════════════════════════ */

  function guardFormSubmit(e) {
    if (isProcessing || !cartData) return;

    var form = e.target;
    if (!form || form.tagName !== 'FORM') return;

    var action = (form.getAttribute('action') || '').toLowerCase();
    if (action.indexOf('/cart') === -1 && action.indexOf('/checkout') === -1) return;

    var checkoutBtn = form.querySelector('[name="checkout"]');
    if (!checkoutBtn) return;

    var bundleCount = getBundleCount(cartData);
    if (bundleCount > 0 && bundleCount < MIN_ITEMS) {
      e.preventDefault();
      e.stopImmediatePropagation();
      alert('Your HiPP bundle requires at least ' + MIN_ITEMS + ' items. You currently have ' + bundleCount + '. Please add ' + (MIN_ITEMS - bundleCount) + ' more item(s) or remove all bundle items before checking out.');
      return false;
    }
  }

  /* ═══════════════════════════════════════════
     Layer 4: Fetch monkey-patch safety net
     Catches /cart/change.js calls that slip past click handlers
     ═══════════════════════════════════════════ */

  var originalFetch = window.fetch;
  window.fetch = function () {
    var args = arguments;
    var url = args[0];
    var opts = args[1] || {};

    // Only intercept cart change/update calls (NOT /cart/add.js or /cart.js)
    if (typeof url === 'string' && cartData && !isProcessing &&
        (url.indexOf('/cart/change.js') !== -1 || url.indexOf('/change.js') !== -1)) {

      var bundleItems = getBundleItems(cartData);
      if (bundleItems.length > 0) {
        try {
          var body = typeof opts.body === 'string' ? JSON.parse(opts.body) : opts.body;
          if (body) {
            var changeKey = null;
            var changeQty = body.quantity != null ? parseInt(body.quantity, 10) : null;

            // Theme uses { line: "N", quantity: Q }
            if (body.line != null) {
              var lineIndex = parseInt(body.line, 10) - 1;
              if (cartData.items[lineIndex]) {
                changeKey = cartData.items[lineIndex].key;
              }
            } else if (body.id) {
              changeKey = body.id;
            }

            if (changeKey && isKeyBundle(changeKey) && changeQty != null) {
              var newCount = computeCountForQuantity(changeKey, changeQty);
              if (newCount > 0 && newCount < MIN_ITEMS) {
                // Block this — show confirmation
                if (confirm('This change will drop your bundle below ' + MIN_ITEMS + ' items. This will remove all bundle items from your cart. Continue?')) {
                  removeAllBundleItems();
                }
                // Return fake response to prevent theme from updating UI
                return Promise.resolve(new Response(JSON.stringify(cartData), {
                  status: 200,
                  headers: { 'Content-Type': 'application/json' }
                }));
              }
            }
          }
        } catch (ex) {
          // Parse error — let it through
        }
      }
    }

    // Also intercept /cart/update.js
    if (typeof url === 'string' && cartData && !isProcessing &&
        url.indexOf('/cart/update.js') !== -1) {
      var bundleItems2 = getBundleItems(cartData);
      if (bundleItems2.length > 0) {
        try {
          var body2 = typeof opts.body === 'string' ? JSON.parse(opts.body) : opts.body;
          if (body2 && body2.updates) {
            var projectedCount = 0;
            for (var i = 0; i < bundleItems2.length; i++) {
              var bKey = bundleItems2[i].key;
              if (bKey in body2.updates) {
                projectedCount += parseInt(body2.updates[bKey], 10) || 0;
              } else {
                projectedCount += bundleItems2[i].quantity;
              }
            }
            if (projectedCount > 0 && projectedCount < MIN_ITEMS) {
              if (confirm('This change will drop your bundle below ' + MIN_ITEMS + ' items. This will remove all bundle items from your cart. Continue?')) {
                removeAllBundleItems();
              }
              return Promise.resolve(new Response(JSON.stringify(cartData), {
                status: 200,
                headers: { 'Content-Type': 'application/json' }
              }));
            }
          }
        } catch (ex) {}
      }
    }

    // Pass through to real fetch
    var result = originalFetch.apply(this, args);

    // Keep cartData fresh — ONLY from full cart responses
    // (NOT /cart/add.js which returns partial data)
    if (typeof url === 'string' &&
        (url.indexOf('/cart.js') !== -1 || url.indexOf('/change.js') !== -1 || url.indexOf('/update.js') !== -1) &&
        url.indexOf('/cart/add') === -1) {
      result = result.then(function (response) {
        var clone = response.clone();
        clone.json().then(function (data) {
          if (isFullCart(data)) {
            cartData = data;
          }
        }).catch(function () {});
        return response;
      });
    }

    return result;
  };

  /* ─── Keep cart data fresh ─── */

  // Theme cart change event
  document.addEventListener('theme:cart:change', function (e) {
    if (e.detail && e.detail.cart && isFullCart(e.detail.cart)) {
      cartData = e.detail.cart;
    } else {
      refreshCart();
    }
  });

  // theme:drawer:open does NOT bubble (bubbles: false), so we must listen
  // on the actual drawer element, not on document.
  function watchDrawer() {
    var drawer = document.querySelector('[data-drawer="drawer-cart"]');
    if (drawer) {
      drawer.addEventListener('theme:drawer:open', function () {
        refreshCart();
      });
    }
  }

  // Also poll-refresh when BYOB addToCart completes (it dispatches theme:cart:reload)
  document.addEventListener('theme:cart:reload', function () {
    refreshCart();
  });

  /* ─── Init ─── */

  function init() {
    // Fetch initial cart data
    refreshCart();

    // Watch the drawer element for open events
    watchDrawer();

    // Capture phase — fire before any theme JS
    document.addEventListener('click', interceptClick, true);
    document.addEventListener('change', guardInput, true);
    document.addEventListener('submit', guardFormSubmit, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
